import {
  DataSourceSettingsDto,
  DashboardDto,
  DashboardMutatePayload,
  MutateWidgetPayload,
  MutateVariablePayload,
  VariableDto,
  VariableSourceSettingsDto,
  WidgetDto,
} from '@thingslog/repositories';
import React, { FC } from 'react';
import { DashboardSidebarWidget } from './widgets/DashboardSidebarWidget';
import { DashboardWidgetSidebarItem } from '.';
import { ResizableDashboardWidgetSidebarContainer } from './ResizableDashboardWidgetSidebarContainer';

interface MutationCallbacks {
  onSuccess?: () => void;
}

interface Props {
  dashboard: DashboardDto;
  open: boolean;
  widgets: DashboardWidgetSidebarItem[];
  dashboardWidgets: WidgetDto[];
  dataSources: DataSourceSettingsDto[];
  variables: VariableDto[];
  variableSources: VariableSourceSettingsDto[];
  companyId?: number | null;
  onAddVariable: () => void;
  onEditVariable: (variable: VariableDto) => void;
  onDeleteVariable: (variable: VariableDto, callbacks?: MutationCallbacks) => void;
  onPersistVariable: (
    id: string,
    payload: MutateVariablePayload,
    callbacks?: MutationCallbacks
  ) => void;
  onTempSaveVariable: (id: string, payload: MutateVariablePayload) => void;
  onPersistNewVariable: (payload: MutateVariablePayload, callbacks?: MutationCallbacks) => void;
  isPersistingVariable: boolean;
  isPersistingNewVariable: boolean;
  isDeletingVariable: boolean;
  onPersistDashboard: (
    dashboardId: string,
    payload: DashboardMutatePayload,
    callbacks?: MutationCallbacks
  ) => void;
  onPersistNewDashboard: (payload: DashboardMutatePayload, callbacks?: MutationCallbacks) => void;
  isPersistingDashboard: boolean;
  isPersistingNewDashboard: boolean;
  onAddDashboardWidget: () => void;
  onEditDashboardWidget: (dashboardWidget: WidgetDto) => void;
  onPersistWidget: (
    widgetId: string,
    payload: MutateWidgetPayload,
    callbacks?: MutationCallbacks
  ) => void;
  onPersistNewWidget: (payload: MutateWidgetPayload, callbacks?: MutationCallbacks) => void;
  onDeleteWidget: (widget: WidgetDto, callbacks?: MutationCallbacks) => void;
  isPersistingWidget: boolean;
  isPersistingNewWidget: boolean;
  isDeletingWidget: boolean;
  onCloseWidget: (widgetId: string) => void;
  onStartWidgetTour: (widgetId: string) => void;
  isMobileRootWidget?: (widget: DashboardWidgetSidebarItem) => boolean;
  className?: string;
}

export const DashboardWidgetSidebar: FC<Props> = ({
  open,
  className,
  dashboard,
  widgets,
  dashboardWidgets,
  dataSources,
  variables,
  variableSources,
  companyId,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
  onPersistVariable,
  onTempSaveVariable,
  onPersistNewVariable,
  isPersistingVariable,
  isPersistingNewVariable,
  isDeletingVariable,
  onPersistDashboard,
  onPersistNewDashboard,
  isPersistingDashboard,
  isPersistingNewDashboard,
  onAddDashboardWidget,
  onEditDashboardWidget,
  onPersistWidget,
  onPersistNewWidget,
  onDeleteWidget,
  isPersistingWidget,
  isPersistingNewWidget,
  isDeletingWidget,
  onCloseWidget,
  onStartWidgetTour,
  isMobileRootWidget,
}: Props) => {
  return (
    <ResizableDashboardWidgetSidebarContainer open={open} className={className}>
      {widgets.map((widget: DashboardWidgetSidebarItem) => (
        <DashboardSidebarWidget
          key={widget.id}
          widget={widget}
          dashboard={dashboard}
          variables={variables}
          variableSources={variableSources}
          dashboardWidgets={dashboardWidgets}
          dataSources={dataSources}
          companyId={companyId}
          onAddVariable={onAddVariable}
          onEditVariable={onEditVariable}
          onDeleteVariable={onDeleteVariable}
          onPersistVariable={onPersistVariable}
          onTempSaveVariable={onTempSaveVariable}
          onPersistNewVariable={onPersistNewVariable}
          isPersistingVariable={isPersistingVariable}
          isPersistingNewVariable={isPersistingNewVariable}
          isDeletingVariable={isDeletingVariable}
          onPersistDashboard={onPersistDashboard}
          onPersistNewDashboard={onPersistNewDashboard}
          isPersistingDashboard={isPersistingDashboard}
          isPersistingNewDashboard={isPersistingNewDashboard}
          onAddDashboardWidget={onAddDashboardWidget}
          onEditDashboardWidget={onEditDashboardWidget}
          onPersistWidget={onPersistWidget}
          onPersistNewWidget={onPersistNewWidget}
          onDeleteWidget={onDeleteWidget}
          isPersistingWidget={isPersistingWidget}
          isPersistingNewWidget={isPersistingNewWidget}
          isDeletingWidget={isDeletingWidget}
          onClose={onCloseWidget}
          onStartWidgetTour={onStartWidgetTour}
          isMobileRootWidget={isMobileRootWidget}
        />
      ))}
    </ResizableDashboardWidgetSidebarContainer>
  );
};
